import React, { useEffect, useState } from "react";
import { getTabBarSmDownTop } from "../../../helpers/tools";
import TabBar from "../../molecules/TabBar";
import { colors } from "../../../styles/variables";
import { isCapacitorNative } from "../../../helpers/isCapacitorNative";
import Tab from "../Tab";
import { TabHolder } from "./style";

export interface TranslatedTabProps {
  text: string;
  url: string;
  exact?: boolean;
}

export interface TabsProps {
  tabs: TranslatedTabProps[];
  color?: string;
}

const Tabs = ({ tabs, color }: TabsProps) => {
  // On Capacitor the global header is permanently hidden, so the
  // show-on-scroll-up logic doesn't apply: the tab strip should just stick
  // at top and stay there. Setting showHeaderBar to false from the start
  // also avoids a transient mid-scroll state where the sticky `top` value
  // is animating between header.height and 0, which leaves a brief gap
  // letting the page scroll bleed through behind the notch.
  const native = isCapacitorNative();
  const [showHeaderBar, setShowHeaderBar] = useState<boolean>(!native);
  const [yValInflectionPoint, setYValInflectionPoint] = useState<number>(0);

  useEffect(() => {
    if (native) return undefined;
    // Show/hide header on scroll
    window.addEventListener("scroll", handleScroll);

    // Cleanup/unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [yValInflectionPoint, native]);

  /**
   * Handler for scroll event listeners, to set state to show or hide Header bar.
   */
  const handleScroll = () => {
    const yValue = +window.scrollY;
    setYValInflectionPoint(yValue);

    // To fix the flickering issue while slow scrolling in Google Chrome,
    // we only show/hide header, search and filter bar,
    // after customers scroll at least 250px.
    if (yValue > yValInflectionPoint) {
      setShowHeaderBar(false);
    } else if (yValue < yValInflectionPoint) {
      setShowHeaderBar(true);
    }
  };

  const bgColor = color || colors.coral;

  return (
    <TabHolder
      isHeaderShown={showHeaderBar}
      smDownTop={getTabBarSmDownTop("0px", showHeaderBar)}
      bgColor={bgColor}
    >
      <TabBar backgroundColor={bgColor}>
        {tabs.map((tab: TranslatedTabProps) => {
          const { text, url, exact } = tab || {};
          return (
            <Tab to={url} exact={exact} key={text}>
              {text}
            </Tab>
          );
        })}
      </TabBar>
    </TabHolder>
  );
};

export default Tabs;
